import { lastItem } from "core/util/ArrayUtil";
import { CourseWrapper, IEntry } from "../courseWrapper/CourseWrapper";

export interface IFile {
	id?: string;
}

export interface IGradeScale {
	id?: string;
	name?: string;
	min?: number;
	max?: number;
	thresholds?: IGradeScaleThreshold[];
}

export interface IGradeScaleThreshold {
	id?: string;
	label?: string;
	value?: number;
}

export interface IAssignmentSubmission {
	id?: string;
	score?: number;
	createdOn?: string;
	feedback?: IFile;
	retryAllowed?: boolean;
	gradeScale?: IGradeScale;
}

export interface IAssignment {
	id?: string;
	externalId?: string;
	name?: string;
	description?: string;
	startDate?: string;
	endDate?: string;
	retryDate?: string;
	gradeScaleEnabled?: boolean;
	deleted?: boolean;
	entry?: IEntry;
	handout?: IFile;
	gradeScale?: IGradeScale;
	submissions?: IAssignmentSubmission[];
}

interface IAssignmentWrapperArgs {
	assignment: IAssignment;
}

export class AssignmentWrapper {
	private assignment: IAssignment;

	private static day: number = (1000 * 60 * 60 * 24);

	public static forAssignment(assignment: IAssignment) {
		return assignment ? new AssignmentWrapper({assignment}) : undefined;
	}

	constructor(args: IAssignmentWrapperArgs) {
		this.assignment = args.assignment;
	}

	public getName(): string {
		return this.assignment.name || undefined;
	}

	public getEndDate(): string {
		return this.assignment.endDate || null;
	}

	public getCourse(): CourseWrapper {
		return CourseWrapper.forEntry(this.assignment.entry);
	}

	public getGradeScale(): IGradeScale {
		return this.assignment.gradeScale || {};
	}

	/**
	 * get count of days until due, rounded down.
	 */
	public getDaysUntilDue(): number {
		const seconds: number = Date.parse(this.getEndDate());
		if (!isNaN(seconds)) {
			const distance = seconds - Date.now();
			return Math.floor(distance / AssignmentWrapper.day);
		} else {
			return 0;
		}
	}

	public getSubmissions(): IAssignmentSubmission[] {
		return (this.assignment && this.assignment.submissions) || [];
	}

	public getLatestSubmission(): IAssignmentSubmission {
		return lastItem(this.getSubmissions());
	}

	public getLatestScore(): number {
		const latestSub = this.getLatestSubmission();
		return latestSub ? latestSub.score : undefined;
	}

	public getLatestScoreText(): string {
		return this.getGradeScaleValue(this.getLatestScore());
	}

	public isComplete(): boolean {
		return this.getLatestScore() !== undefined;
	}

	public isGradeScale(): boolean {
		return this.assignment.gradeScaleEnabled && this.getGradeScale().thresholds !== undefined;
	}

	private getGradeScaleValue(score?: number): string | undefined {
		if (score) {
			if (this.isGradeScale()) {
				return this.getGradeScale().thresholds.find((threshold) => {
					return score > threshold.value;
				}).label;
			} else {
				return score.toString();
			}
		} else {
			return undefined;
		}
	}
}



// WEBPACK FOOTER //
// ./src/common/entities/assignmentWrapper/AssignmentWrapper.ts